import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useAppActions, useAppState, PolygonEntityType } from '../app/appContext';
import { useAutoScale } from './canvas/hooks/useAutoScale';
import { useCanvasData } from '../hooks/canvas/useCanvasData';
import { usePolygonEditor } from '../hooks/canvas/usePolygonEditor';
import { usePolygonCreation } from '../hooks/canvas/usePolygonCreation';
import { useCanvasCallbacks } from '../hooks/canvas/useCanvasCallbacks';
import { MODE_TO_TYPE, CHECKOUT_ENTITY_TYPES, CHECKOUT_TYPE_TO_ENTITY } from '../utils/polygonTypes';
import CanvasStage from './canvas/CanvasStage';
import { stageSizePx } from '../utils/canvasGutter';
import { useT } from '@/contexts/I18nContext';
import UnifiedEditForm from './UnifiedEditForm';
import { useToolbarSlot } from './zoning/toolbarSlot';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { hotkeyBlocked } from './zoning/hotkeys';
import './CanvasRenderer.css';

// Ray-casting hit test for cycling through overlapping polygons.
function pointInPolygon(p: [number, number], poly: [number, number][]): boolean {
  if (poly.length < 3) return false;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1];
    const xj = poly[j][0], yj = poly[j][1];
    const intersect = ((yi > p[1]) !== (yj > p[1])) &&
      (p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi || Number.EPSILON) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

const CanvasRenderer = () => {
  const { t } = useT();
  const actions = useAppActions();
  const appState = useAppState();
  const { polygons, camera, frameLoading, pendingEdit } = appState;
  const [snackError, setSnackError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<{ polygonId: string; type: PolygonEntityType } | null>(null);
  const handleError = useCallback((msg: string) => setSnackError(msg), []);
  const frame = polygons.areas.frame;
  // Memoize Object.values so downstream useMemo's (useCanvasData,
  // orderedPolygons, …) keep stable references when only local state
  // (e.g. editing in-progress points) changes. Without this, every
  // CanvasRenderer render re-creates these arrays, defeating
  // PolygonLayer's React.memo and forcing full re-renders on every
  // drag pixel and marquee move.
  const areas = useMemo(() => Object.values(polygons.areas.entities), [polygons.areas.entities]);
  const racks = useMemo(() => Object.values(polygons.racks.entities), [polygons.racks.entities]);
  const shelves = useMemo(() => Object.values(polygons.shelves.entities), [polygons.shelves.entities]);
  const tables = useMemo(() => Object.values(polygons.tables.entities), [polygons.tables.entities]);
  const checkoutZones = useMemo(() => Object.values(polygons.checkoutZones.entities), [polygons.checkoutZones.entities]);
  const selectedPolygon = polygons.ui.selectedPolygon;
  const mode = polygons.ui.mode;
  const selectedCameraId = camera.selectedCameraId;
  // Image cache: avoid re-downloading when switching back to a previously visited camera
  const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const [image, setImage] = useState<HTMLImageElement | undefined>(undefined);
  useEffect(() => {
    const url = frame?.url || '';
    if (!url) { setImage(undefined); return; }
    const cached = imageCacheRef.current.get(url);
    if (cached) { setImage(cached); return; }
    const img = new window.Image();
    img.onload = () => { imageCacheRef.current.set(url, img); setImage(img); };
    img.onerror = () => setImage(undefined);
    img.src = url;
  }, [frame?.url]);
  const containerRef = useRef<HTMLDivElement>(null);

  const { standaloneAreas, filteredRacks, filteredShelves, filteredTables, filteredCheckoutZones, areaMap } = useCanvasData(
    areas, racks, shelves, tables, checkoutZones, selectedCameraId
  );

  const {
    editingPolygons,
    activeEditingPolygonId,
    activeEditingType,
    selectedPoints,
    saving: editingSaving,
    isDirty,
    startEditing,
    handlePointSelect,
    handleSelectPointsInRect,
    clearPointSelection,
    handlePointDragStart,
    handlePointDrag,
    handlePolygonDragStart,
    handlePolygonDragMove,
    handlePolygonDrag,
    handlePointDragEnd,
    handleSaveEdit,
    handleDeletePolygon,
    cancelEditing,
    handleEdgeClick,
    deleteSelectedPoints,
    undo,
  } = usePolygonEditor({ onError: handleError });

  const {
    tempPoints,
    saving: creationSaving,
    editFormOpen,
    selectedRackForShelf,
    awaitingRackSelection,
    handleStageClick,
    handleRackClickForShelf,
    handleCancelPolygon,
    handleCancelShelf,
    setEditFormOpen,
    setTempPoints,
    setSelectedRackForShelf,
    setAwaitingRackSelection
  } = usePolygonCreation();

  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');

  const { getAreaPoints, handlePolygonClick } = useCanvasCallbacks({
    areas,
    standaloneAreas,
    filteredRacks,
    filteredShelves,
    filteredTables,
    filteredCheckoutZones,
    areaMap
  });

  useEffect(() => {
    if (image && frame && (frame.width !== image.width || frame.height !== image.height)) {
      actions.setFrame({ ...frame, width: image.width, height: image.height });
    }
  }, [image, frame, actions]);

  // Auto-start editing from sidebar click. If the request asked to open the
  // edit-details dialog (three-dot menu Edit action), open it after editing is
  // set up — mirrors what the toolbar's "Редактировать" button does.
  //
  // The kebab-Edit request may target a polygon whose camera/frame isn't
  // loaded yet (handleClickItem switches camera for cross-camera polygons), so
  // the frame `image` and the polygon's points arrive asynchronously. We must
  // NOT early-return-and-forget: keep `pendingEdit` PENDING until BOTH the
  // image AND the resolved points are available, then consume it exactly once
  // by starting editing + opening the form. The effect re-runs as image/points
  // arrive (they're in the dep list), so it reliably ends at UnifiedEditForm —
  // matching the toolbar "Редактировать" path — every time.
  useEffect(() => {
    if (!pendingEdit) return;
    // Wait for the target camera's frame to load before resolving points.
    if (!image) return;
    const { entityId, entityType, openDetails } = pendingEdit;
    const points = getAreaPoints(
      entityType === 'standalone' ? entityId :
      entityType === 'rack' ? (filteredRacks.find(r => r.id === entityId)?.area || '') :
      entityType === 'shelf' ? (filteredShelves.find(s => s.id === entityId)?.area || '') :
      entityType === 'table' ? (filteredTables.find(t => t.id === entityId)?.area || '') :
      (filteredCheckoutZones.find(cz => cz.id === entityId)?.areas[0] || '')
    );
    // Points not yet resolved (the polygon's area hasn't landed in state for
    // the freshly-switched camera). Keep pendingEdit pending; this effect will
    // re-run when filteredRacks/… update and try again.
    if (points.length === 0) return;
    startEditing(entityId, entityType, points, false);
    actions.clearPendingEdit();
    if (openDetails) {
      setFormMode('edit');
      setEditFormOpen(true);
    }
  }, [pendingEdit, image, filteredRacks, filteredShelves, filteredTables, filteredCheckoutZones, getAreaPoints, startEditing, actions, setEditFormOpen]);

  useEffect(() => {
    if (mode && mode.startsWith('create_') && editingPolygons.length > 0) {
      cancelEditing();
    }
    if (mode === 'create_shelf') {
      setAwaitingRackSelection(true);
      setSelectedRackForShelf(null);
      setTempPoints([]);
    } else {
      setAwaitingRackSelection(false);
      setSelectedRackForShelf(null);
    }
  }, [mode, setAwaitingRackSelection, setSelectedRackForShelf, setTempPoints, editingPolygons.length, cancelEditing]);

  useEffect(() => {
    if (mode === 'view') {
      setTempPoints([]);
      setSelectedRackForShelf(null);
      setAwaitingRackSelection(false);
    }
  }, [mode, setTempPoints, setSelectedRackForShelf, setAwaitingRackSelection]);

  // Clear temp points and editing when camera changes (points belong to previous frame)
  useEffect(() => {
    setTempPoints([]);
    if (editingPolygons.length > 0) cancelEditing();
    if (mode === 'create_shelf') {
      setSelectedRackForShelf(null);
      setAwaitingRackSelection(true);
    }
  }, [selectedCameraId]); // intentionally depends only on selectedCameraId

  const isCreationMode = mode && mode.startsWith('create_');
  const creationTypeFromMode = MODE_TO_TYPE[mode] || 'standalone';

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (mode?.startsWith('create_')) e.preventDefault();
  }, [mode]);

  // Масштаб подбирается под холст С ПОЛЕМ вокруг картинки (см. canvasGutter):
  // иначе поле вылезало бы за контейнер.
  const scale = useAutoScale(containerRef, image ? stageSizePx({ width: image.width, height: image.height }) : null);
  const saving = editingSaving || creationSaving;

  // ── Resolve editing entities ───────────────────────────────────────────────

  const editingRack = useMemo(() => (
    activeEditingType === 'rack' && activeEditingPolygonId != null
      ? filteredRacks.find(r => r.id === activeEditingPolygonId) || null
      : null
  ), [activeEditingType, activeEditingPolygonId, filteredRacks]);

  const editingShelf = useMemo(() => (
    activeEditingType === 'shelf' && activeEditingPolygonId != null
      ? filteredShelves.find(s => s.id === activeEditingPolygonId) || null
      : null
  ), [activeEditingType, activeEditingPolygonId, filteredShelves]);

  const editingTable = useMemo(() => (
    activeEditingType === 'table' && activeEditingPolygonId != null
      ? filteredTables.find(t => t.id === activeEditingPolygonId) || null
      : null
  ), [activeEditingType, activeEditingPolygonId, filteredTables]);

  const editingArea = useMemo(() => (
    activeEditingType === 'standalone' && activeEditingPolygonId != null
      ? standaloneAreas.find(a => a.id === activeEditingPolygonId) || null
      : null
  ), [activeEditingType, activeEditingPolygonId, standaloneAreas]);

  const editingCheckoutZone = useMemo(() => (
    activeEditingType && CHECKOUT_ENTITY_TYPES.includes(activeEditingType) && activeEditingPolygonId != null
      ? filteredCheckoutZones.find(cz => cz.id === activeEditingPolygonId) || null
      : null
  ), [activeEditingType, activeEditingPolygonId, filteredCheckoutZones]);

  const canOpenEditDetails = Boolean(
    editingPolygons.length === 1 &&
    (editingRack || editingShelf || editingTable || editingArea || editingCheckoutZone)
  );

  // ── Callbacks ──────────────────────────────────────────────────────────────

  const onStageClick = (e: any) => {
    handleStageClick(e, mode, frame, image, scale);
  };

  const onRackClickForShelf = (rackId: string) => {
    handleRackClickForShelf(rackId, mode);
  };

  // Render-order list of all polygons currently visible. Used for hit-testing
  // when cycling through overlapping polygons under the cursor.
  const orderedPolygons = useMemo(() => {
    const out: { id: string; type: PolygonEntityType; points: [number, number][] }[] = [];
    for (const a of standaloneAreas) out.push({ id: a.id, type: 'standalone', points: a.points });
    for (const r of filteredRacks) out.push({ id: r.id, type: 'rack', points: getAreaPoints(r.area) });
    for (const s of filteredShelves) out.push({ id: s.id, type: 'shelf', points: getAreaPoints(s.area) });
    for (const t of filteredTables) out.push({ id: t.id, type: 'table', points: getAreaPoints(t.area) });
    for (const cz of filteredCheckoutZones) {
      const entityType = CHECKOUT_TYPE_TO_ENTITY[cz.type] || 'queue';
      for (const aid of cz.areas) {
        const pts = getAreaPoints(aid);
        if (pts.length > 0) out.push({ id: cz.id, type: entityType, points: pts });
      }
    }
    return out;
  }, [standaloneAreas, filteredRacks, filteredShelves, filteredTables, filteredCheckoutZones, getAreaPoints]);

  const startSwitchTo = useCallback((polygonId: string, type: PolygonEntityType) => {
    handlePolygonClick(polygonId, type, false, startEditing);
  }, [handlePolygonClick, startEditing]);

  const onPolygonClick = (
    polygonId: string,
    type: PolygonEntityType,
    extend: boolean,
    normalizedPos: { x: number; y: number } | null,
  ) => {
    if (isCreationMode) return;

    const isAlreadyEditing = editingPolygons.some(p => p.id === polygonId && p.type === type);

    // If the click landed on an editable polygon's edge, insert a vertex there
    // and stop. This is what lets the user click directly ON an edge to add a
    // point (without having to fish for the empty-canvas zone next to it).
    // The handler skips 4-vertex polygons internally so rectangles stay
    // rectangles. Shift-clicks fall through so the multi-select shift gesture
    // is preserved.
    if (isAlreadyEditing && !extend && normalizedPos && handleEdgeClick(normalizedPos)) {
      return;
    }

    // Cycle through overlapping polygons under the cursor. Konva only ever
    // dispatches the click to the TOP-MOST Line, so `polygonId`/`type` is the
    // top polygon regardless of what is currently selected. We therefore drive
    // cycling from the CURRENTLY-ACTIVE polygon (activeEditingPolygonId/Type),
    // not the clicked one — otherwise selection oscillates top↔next and never
    // reaches the 3rd+ in the stack.
    if (!extend && normalizedPos) {
      const candidates = orderedPolygons.filter(p => pointInPolygon([normalizedPos.x, normalizedPos.y], p.points));
      if (candidates.length > 0) {
        const activeIdx = candidates.findIndex(c => c.id === activeEditingPolygonId && c.type === activeEditingType);
        if (activeIdx >= 0) {
          // The active polygon is part of the stack under the cursor → advance
          // from its index, wrapping through ALL candidates (A→B→C→A).
          if (candidates.length > 1) {
            const next = candidates[(activeIdx + 1) % candidates.length];
            if (next.id !== activeEditingPolygonId || next.type !== activeEditingType) {
              if (isDirty) setPendingSwitch({ polygonId: next.id, type: next.type });
              else startSwitchTo(next.id, next.type);
            }
          }
          return;
        }
        // No active polygon in this stack (first click) → select the clicked
        // (top-most) polygon, preserving the existing first-select behavior.
        // Konva already dispatched to the visually top-most Line, so the
        // clicked polygonId/type is exactly what the user pointed at.
        if (!isAlreadyEditing && isDirty) {
          setPendingSwitch({ polygonId, type });
        } else {
          handlePolygonClick(polygonId, type, extend, startEditing);
        }
        return;
      }
    }

    // Switching to a different polygon while there are unsaved edits → confirm.
    if (!extend && !isAlreadyEditing && isDirty) {
      setPendingSwitch({ polygonId, type });
      return;
    }

    handlePolygonClick(polygonId, type, extend, startEditing);
  };

  const confirmDiscardSwitch = () => {
    if (!pendingSwitch) return;
    const { polygonId, type } = pendingSwitch;
    setPendingSwitch(null);
    cancelEditing();
    // Defer to the next tick so cancelEditing's state update lands first.
    setTimeout(() => startSwitchTo(polygonId, type), 0);
  };

  const cancelDiscardSwitch = () => setPendingSwitch(null);

  const onSaveEdit = useCallback(() => {
    // areas.entities — исходная геометрия до правки: по ней полка единственной полки
    // стеллажа пересчитывается вслед за стеллажом (#1547).
    handleSaveEdit(filteredRacks, filteredShelves, filteredTables, filteredCheckoutZones, selectedCameraId, polygons.areas.entities);
  }, [handleSaveEdit, filteredRacks, filteredShelves, filteredTables, filteredCheckoutZones, selectedCameraId, polygons.areas.entities]);

  const onDeletePolygon = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  // Keyboard shortcuts: Delete/Backspace removes selected points (Delete with
  // no points selected → delete the polygon, like the «Удалить» button with its
  // Del badge), Escape cancels. Nothing fires while typing or under a dialog.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (hotkeyBlocked(e)) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedPoints.length > 0) {
          e.preventDefault();
          deleteSelectedPoints();
        } else if (e.key === 'Delete' && editingPolygons.length === 1 && !saving) {
          e.preventDefault();
          onDeletePolygon();
        }
      }
      if ((e.key === 'z' || e.key === 'я') && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if (e.key === 'Escape') {
        if (editingPolygons.length > 0) {
          cancelEditing();
        } else if (isCreationMode) {
          if (tempPoints.length > 0) {
            handleCancelPolygon();
          } else {
            actions.setMode('view');
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPoints, deleteSelectedPoints, editingPolygons, cancelEditing, tempPoints, handleCancelPolygon, undo, isCreationMode, actions, saving, onDeletePolygon]);

  const confirmDelete = () => {
    setDeleteConfirmOpen(false);
    handleDeletePolygon(racks, shelves, tables, checkoutZones, selectedCameraId);
  };

  // Open unified form for creation
  const openCreateForm = useCallback(() => {
    setFormMode('create');
    setEditFormOpen(true);
  }, [setEditFormOpen]);

  // Open unified form for editing
  const openEditDetails = useCallback(() => {
    setFormMode('edit');
    setEditFormOpen(true);
  }, [setEditFormOpen]);

  const handleFormClose = () => {
    setEditFormOpen(false);
    setFormMode('create');
  };

  const handleFormSuccess = () => {
    if (formMode === 'create') {
      setTempPoints([]);
      if (mode === 'create_shelf') {
        setSelectedRackForShelf(null);
        setAwaitingRackSelection(true);
      }
    } else {
      cancelEditing();
    }
    setEditFormOpen(false);
    setFormMode('create');
  };

  const formType: PolygonEntityType = formMode === 'create'
    ? creationTypeFromMode
    : (activeEditingType || 'standalone');

  // ── Toolbar slot bridge — push state to page-level toolbar ────────────────
  const toolbarSlot = useToolbarSlot();
  const setSlot = toolbarSlot.setState;

  const handleCreationCancel = useCallback(() => {
    if (mode === 'create_shelf') handleCancelShelf();
    else handleCancelPolygon();
  }, [mode, handleCancelPolygon, handleCancelShelf]);

  const handleCreationExit = useCallback(() => actions.setMode('view'), [actions]);

  const handleEntityAction = useCallback((key: 'edit' | 'delete') => {
    if (key === 'edit') openEditDetails();
    else if (key === 'delete') onDeletePolygon();
  }, [openEditDetails, onDeletePolygon]);

  const isEntityActionAvailable = useCallback((key: 'edit' | 'delete') => {
    if (key === 'edit')   return canOpenEditDetails;
    if (key === 'delete') return editingPolygons.length === 1;
    return true;
  }, [canOpenEditDetails, editingPolygons.length]);

  useEffect(() => {
    if (isCreationMode) {
      setSlot({
        kind: 'creation',
        entityType: creationTypeFromMode,
        pointCount: tempPoints.length,
        awaitingRackSelection,
        rackSelected: selectedRackForShelf != null,
        onCancel: handleCreationCancel,
        onSave: openCreateForm,
        onExit: handleCreationExit,
      });
    } else if (editingPolygons.length > 0) {
      setSlot({
        kind: 'edit',
        activeType: activeEditingType,
        saving,
        onCancel: cancelEditing,
        onSave: onSaveEdit,
        onAction: handleEntityAction,
        isAvailable: isEntityActionAvailable,
      });
    } else {
      setSlot(null);
    }
    return () => setSlot(null);
  }, [
    isCreationMode, creationTypeFromMode, tempPoints.length, awaitingRackSelection, selectedRackForShelf,
    editingPolygons.length, activeEditingType, saving,
    handleCreationCancel, openCreateForm, handleCreationExit,
    cancelEditing, onSaveEdit, handleEntityAction, isEntityActionAvailable,
    setSlot,
  ]);

  return (
    <div className="flex h-full w-full flex-1 min-h-0 flex-col">
      <div ref={containerRef} onContextMenu={handleContextMenu} className="relative flex flex-1 min-h-0 w-full items-center justify-center overflow-hidden">
        {!frame ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-muted-foreground">
            {t('drawing.selectCamera')}
          </div>
        ) : !image ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-muted-foreground">
            {t('drawing.loadingImage')}
          </div>
        ) : (
          <CanvasStage
            image={image}
            scale={scale}
            mode={mode}
            selectedPolygon={selectedPolygon}
            onStageClick={onStageClick}
            editingPolygons={editingPolygons}
            selectedPoints={selectedPoints}
            onPointSelect={handlePointSelect}
            onPointDragStart={handlePointDragStart}
            onPointDrag={handlePointDrag}
            onPolygonDragStart={handlePolygonDragStart}
            onPolygonDragMove={handlePolygonDragMove}
            onPolygonDrag={handlePolygonDrag}
            onPointDragEnd={handlePointDragEnd}
            onPolygonClick={onPolygonClick}
            onEdgeClick={handleEdgeClick}
            tempPoints={tempPoints}
            selectedRackForShelf={selectedRackForShelf}
            awaitingRackSelection={awaitingRackSelection}
            onRackClickForShelf={onRackClickForShelf}
            onTempPointDrag={(idx, x, y) => setTempPoints(tempPoints.map((p, i) => i === idx ? [x, y] : p))}
            standaloneAreas={standaloneAreas}
            filteredRacks={filteredRacks}
            filteredShelves={filteredShelves}
            filteredTables={filteredTables}
            filteredCheckoutZones={filteredCheckoutZones}
            getAreaPoints={getAreaPoints}
            onSelectPointsInRect={handleSelectPointsInRect}
            onClearPointSelection={clearPointSelection}
          />
        )}

        {frameLoading && image && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/25">
            <div className="flex items-center gap-2 rounded-lg bg-card/95 px-4 py-2.5 text-sm shadow-md">
              <Loader2 className="size-4 animate-spin" />
              <span>{t('drawing.loadingFrame')}</span>
            </div>
          </div>
        )}

        {snackError && (
          <div className="pointer-events-auto absolute bottom-4 left-1/2 z-20 -translate-x-1/2 flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive px-4 py-2 text-sm text-destructive-foreground shadow-lg">
            <AlertTriangle className="size-4" />
            <span>{snackError}</span>
            <button
              type="button"
              onClick={() => setSnackError(null)}
              className="ml-2 rounded p-0.5 opacity-80 transition-opacity hover:opacity-100"
              aria-label={t('drawing.cancel')}
            >
              ×
            </button>
          </div>
        )}
      </div>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('drawing.deleteTitle')}</DialogTitle>
            <DialogDescription>{t('drawing.deleteConfirm')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              {t('drawing.cancel')}
            </Button>
            <Button type="button" variant="destructive-solid" onClick={confirmDelete}>
              {t('drawing.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingSwitch !== null} onOpenChange={(open) => { if (!open) cancelDiscardSwitch(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('drawing.discardTitle')}</DialogTitle>
            <DialogDescription>{t('drawing.discardConfirm')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={cancelDiscardSwitch}>
              {t('drawing.cancel')}
            </Button>
            <Button type="button" variant="destructive-solid" onClick={confirmDiscardSwitch}>
              {t('drawing.discard')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnifiedEditForm
        open={editFormOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        initialType={formType}
        tempPoints={formMode === 'create' ? tempPoints : undefined}
        selectedRackId={selectedRackForShelf}
        editingRack={formMode === 'edit' ? editingRack : null}
        editingShelf={formMode === 'edit' ? editingShelf : null}
        editingTable={formMode === 'edit' ? editingTable : null}
        editingArea={formMode === 'edit' ? editingArea : null}
        editingCheckoutZone={formMode === 'edit' ? editingCheckoutZone : null}
      />
    </div>
  );
};

export default CanvasRenderer;
